import { useEffect } from "react";
import { surfaceRoleAttributes, surfaceRoleClassName } from "@/components/ui";
import { requestNativeGlassRefresh } from "@/desktop/native-glass-settings";
import { useReducedTransparency } from "@/hooks/use-reduced-transparency";
import { cn } from "@/lib/cn";
import { isTauri } from "@/lib/is-tauri";
import { isMacPlatform, isWindowsPlatform } from "@/lib/os-platform";
import { useWallFrostOn } from "@/lib/wall-frost";
import "./wall-surface.css";

/**
 * The wall's frost (plan 11 A3): one full-window sheet behind the wall in
 * Workspace mode, mounted with the wall so leaving the Workspace fades it
 * out instead of removing it mid-fade.
 *
 * The frost dims the world behind the Workspace in both themes: one ink
 * tint (`wall_frost_tint.rs`), so white and ink cards both stand out. On
 * macOS it is one native `backdrop` plate (Liquid Glass Clear tinted with the
 * ink, or the Popover blur without Liquid Glass) under every other plate,
 * faded through its opacity; the webview paints nothing over the other
 * plates. On Windows the webview paints the frame surface token, no blur
 * (Juan, 2026-10-28: the system blur turned black over Remote Desktop and
 * grey whenever the overlay was inactive). Elsewhere it paints the tint
 * (`--wall-frost-tint`). All of them fade over 200 ms; under Reduce
 * Transparency the frost is the solid ink.
 */
export function WallFrost() {
  const on = useWallFrostOn();
  const reduced = useReducedTransparency();
  const nativePlate = isTauri() && isMacPlatform();
  const windows = isTauri() && isWindowsPlatform();

  // The plate reads Reduce Transparency natively; re-send it on a change.
  useEffect(() => {
    if (nativePlate) requestNativeGlassRefresh({ forceSend: true });
  }, [nativePlate, reduced]);

  return (
    <div
      aria-hidden="true"
      className={surfaceRoleClassName("backdrop")}
      {...(nativePlate
        ? {
            ...surfaceRoleAttributes("backdrop", {
              nativeLevel: "root",
              nativeGroup: "wall-frost",
              nativeOpacity: on ? 1 : 0,
            }),
          }
        : null)}
      data-platform={windows ? "windows" : undefined}
      data-transparency={reduced ? "reduced" : undefined}
      data-testid="wall-frost"
    >
      <div
        className={cn(
          "wall-frost-tint transition-opacity duration-200",
          on ? "opacity-100" : "opacity-0",
        )}
      />
    </div>
  );
}
